import Koppa from "@koppajs/koppajs-core";

import config from "./config";
import cookieBanner from "./cookie-banner.kpa";

import scriptController from "../script-controller";

Koppa.take(cookieBanner, 'cookie-banner');

declare global {
    interface Window {
        koppaConsentManager?: ConsentManager;
    }
}

class ConsentManager {
    public name = 'consentManager';
    private config: ConsentManagerConfig = config;
    private logging: boolean = false;

    constructor() {
        if (config.logging) this.logging = config.logging;

        const storedConfig = localStorage.getItem('consentConfig');
        
        if (storedConfig) {
            this.config.consentConfig = JSON.parse(storedConfig);
        }

        this.overrideStorageMethods();
        this.overrideCookieMethods();

        this.addThirdPartyTools();

        this.cleanUpDisallowedEntries = this.cleanUpDisallowedEntries.bind(this);

        if (typeof window !== 'undefined') {
            window.koppaConsentManager = this;
        }
    }

    // Überprüft, ob das Banner auf der aktuellen Seite ausgeblendet werden soll
    public shouldHideBanner(): boolean {
        const currentPath = window.location.pathname;
        return this.config.hideBannerOnUrls.includes(currentPath);
    }

    public acceptAllCookies(): void {
        this.updateConsent('preferences', true);
        this.updateConsent('statistics', true);
        this.updateConsent('marketing', true);
    }

    public acceptSelectedCookies(): void {
        // Checkboxen überprüfen und entsprechende Kategorien erlauben
        const preferencesChecked = (document.getElementById('consent-preferences') as HTMLInputElement)?.checked;
        const statisticsChecked = (document.getElementById('consent-statistics') as HTMLInputElement)?.checked;
        const marketingChecked = (document.getElementById('consent-marketing') as HTMLInputElement)?.checked;

        this.updateConsent('preferences', !!preferencesChecked);
        this.updateConsent('statistics', !!statisticsChecked);
        this.updateConsent('marketing', !!marketingChecked);
    }

    public rejectAllCookies(): void {
        // Alle optionalen Kategorien ablehnen
        this.updateConsent('preferences', false);
        this.updateConsent('statistics', false);
        this.updateConsent('marketing', false);
    }

    // Cookie-Banner öffnen
    public openCookieBanner(): void {
        window.dispatchEvent(new CustomEvent('cookieBannerOpen', {
            detail: { message: 'Cookie banner should be opened' }
        }));
    
        window.dispatchEvent(new CustomEvent('cookieBannerShown', {
            detail: { message: 'Cookie banner is shown' }
        }));
    }

    // Cookie-Banner schließen
    public closeCookieBanner(): void {
        window.dispatchEvent(new CustomEvent('cookieBannerClose', {
            detail: { message: 'Cookie banner should be closed' }
        }));
    
        window.dispatchEvent(new CustomEvent('cookieBannerHidden', {
            detail: { message: 'Cookie banner is hidden' }
        }));
    }

    // Loggt blockierte Aktionen, wenn das Logging aktiviert ist
    private logBlockedAction(actionType: string, name: string, details?: string) {
        if (this.logging) { 
            console.log(`[ConsentManager - Blocked] Type: ${actionType}, Name: ${name}, Details: ${details || 'N/A'}`);
        }
    }

    // Überschreibt die Cookie-Methoden, um die Zustimmung zu überprüfen, bevor Cookies gesetzt werden
    private overrideCookieMethods() {
        const originalCookieDescriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie');

        if (originalCookieDescriptor && originalCookieDescriptor.set) {
            const originalCookieSetter = originalCookieDescriptor.set;

            Object.defineProperty(document, 'cookie', {
                configurable: true,
                enumerable: true,
                get() {
                    return originalCookieDescriptor.get ? originalCookieDescriptor.get.call(document) : '';
                },
                set: (value: string) => {
                    const cookieName = value.split('=')[0]?.trim();
                    if (cookieName && this.isCookieAllowed(cookieName)) {
                        originalCookieSetter.call(document, value);
                    } else {
                        this.logBlockedAction('Cookie', cookieName || 'Unknown', value);
                    }
                }
            });
        } else {
            console.error("Could not override document.cookie: Original cookie setter is undefined.");
        }
    }

    // Überschreibt die LocalStorage- und SessionStorage-Methoden, um die Zustimmung zu überprüfen
    private overrideStorageMethods() {
        // Speichere die Originalmethoden für beide Storages
        const originalLocalSetItem = Storage.prototype.setItem.bind(localStorage);
        const originalLocalGetItem = Storage.prototype.getItem.bind(localStorage);
        const originalLocalRemoveItem = Storage.prototype.removeItem.bind(localStorage);

        const originalSessionSetItem = Storage.prototype.setItem.bind(sessionStorage);
        const originalSessionGetItem = Storage.prototype.getItem.bind(sessionStorage);
        const originalSessionRemoveItem = Storage.prototype.removeItem.bind(sessionStorage);

        const isStorageAllowed = this.isStorageAllowed.bind(this);

        Object.defineProperty(Storage.prototype, "setItem", {
            configurable: true,
            enumerable: false,
            writable: true,
            value: function (key: string, value: string) {
                if (this === localStorage) {
                    if (isStorageAllowed(key, "localStorage")) {
                        originalLocalSetItem(key, value);
                    } else {
                        console.warn(`localStorage Blockiert: ${key}`);
                    }
                } else if (this === sessionStorage) {
                    if (isStorageAllowed(key, "sessionStorage")) {
                        originalSessionSetItem(key, value);
                    } else {
                        console.warn(`sessionStorage Blockiert: ${key}`);
                    }
                }
            },
        });

        Object.defineProperty(Storage.prototype, "getItem", {
            configurable: true,
            enumerable: false,
            writable: true,
            value: function (key: string) {
                if (this === localStorage) {
                    if (isStorageAllowed(key, "localStorage")) {
                        return originalLocalGetItem(key);
                    } else {
                        console.warn(`localStorage Zugriff blockiert: ${key}`);
                        return null;
                    }
                } else if (this === sessionStorage) {
                    if (isStorageAllowed(key, "sessionStorage")) {
                        return originalSessionGetItem(key);
                    } else {
                        console.warn(`sessionStorage Zugriff blockiert: ${key}`);
                        return null;
                    }
                }
                return null;
            },
        });

        Object.defineProperty(Storage.prototype, "removeItem", {
            configurable: true,
            enumerable: false,
            writable: true,
            value: function (key: string) {
                if (this === localStorage) {
                    if (isStorageAllowed(key, "localStorage")) {
                        originalLocalRemoveItem(key);
                    } else {
                        console.warn(`localStorage Eintrag blockiert: ${key}`);
                    }
                } else if (this === sessionStorage) {
                    if (isStorageAllowed(key, "sessionStorage")) {
                        originalSessionRemoveItem(key);
                    } else {
                        console.warn(`sessionStorage Eintrag blockiert: ${key}`);
                    }
                }
            },
        });
    }

    // Überprüft, ob die Einwilligung bereits erteilt wurde
    public hasConsentBeenGiven(): boolean {
        return !!localStorage.getItem('consentConfig'); // true, wenn Eintrag existiert
    }

    // Erlaube globale Abfrage von Consent (z. B. für Komponente)
    public hasConsent(category: ConsentCategory): boolean {
        return config.consentConfig?.[category] === true;
    }

    // Speichert die aktuelle Zustimmungskonfiguration im LocalStorage
    private saveConsentConfig() {
        localStorage.setItem('consentConfig', JSON.stringify(this.config.consentConfig));
    }

    // Überprüft, ob die Zustimmung für eine bestimmte Kategorie gegeben ist
    private isConsentGiven(category: ConsentCategory): boolean {
        return this.config.consentConfig[category];
    }

    // Überprüft, ob ein Cookie in der gegebenen Kategorie erlaubt ist
    private isCookieAllowed(name: string): boolean {
        const item = this.config.cookieItems.find(item => item.name === name);
        return item ? this.isConsentGiven(item.category) : false;
    }

    // Überprüft, ob ein Eintrag in einer der Kategorien (localStorage oder sessionStorage) erlaubt ist
    private isStorageAllowed(key: string, storageType: "localStorage" | "sessionStorage"): boolean {
        const storageConfig = storageType === "localStorage" ? config.localStorageItems : config.sessionStorageItems;
        const item = storageConfig.find((item) => item.name === key);
        return item ? this.isConsentGiven(item.category) : false;
    }

    // Löscht ein Cookie mit dem gegebenen Namen, Pfad und optionaler Domain
    private deleteCookie(name: string, path: string = '/', domain?: string) {
        document.cookie = name + "=; Max-Age=-99999999; path=" + path + (domain ? "; domain=" + domain : "");
    }

    // Verwaltet die Drittanbieter-Tools basierend auf der Zustimmung
    private addThirdPartyTools(): void {
        this.config.thirdPartyTools.forEach(tool => {
            if (this.isConsentGiven(tool.category)) {
                // Verwenden des ScriptControllers, um das Skript zu laden
                if (tool.globalVar !== undefined) {
                    scriptController.useScript(tool.scriptUrl, {
                        globalVar: tool.globalVar,
                        attributes: tool.attributes,
                        isModule: tool.isModule,
                        useSecureDOM: tool.useSecureDOM
                    }).catch(error => {
                        console.error(`Fehler beim Laden von ${tool.name}:`, error);
                    });
                }
            }
        });
    }

    // Entfernt alle Drittanbieter-Tools, für die keine Zustimmung erteilt wurde
    private removeThirdPartyTools(): void {
        this.config.thirdPartyTools.forEach(tool => {
            if (!this.isConsentGiven(tool.category)) {
                this.logBlockedAction('Script', tool.name, tool.scriptUrl);
                scriptController.removeScript(tool.scriptUrl, tool.globalVar);

                tool.additionalElements?.forEach(selector => {
                    document.querySelectorAll(selector).forEach(element => element.remove());
                });
            }
        });
    }

    // Bereinigt alle unerlaubten Einträge (Cookies, LocalStorage, SessionStorage) und entfernt unerlaubte Drittanbieter-Tools
    private cleanUpDisallowedEntries() {
        // Unerlaubte Cookies entfernen
        this.config.cookieItems.forEach(item => {
            if (!this.isConsentGiven(item.category)) {
                this.deleteCookie(item.name, item.path, item.domain);
            }
        });

        // Unerlaubte LocalStorageStorage-Einträge entfernen
        this.config.localStorageItems.forEach(item => {
            if (!this.isConsentGiven(item.category)) {
                localStorage.removeItem(item.name);
            }
        });

        // Unerlaubte SessionStorage-Einträge entfernen
        this.config.sessionStorageItems.forEach(item => {
            if (!this.isConsentGiven(item.category)) {
                sessionStorage.removeItem(item.name);
            }
        });

        // Unerlaubte Drittanbieter-Tools entfernen
        this.removeThirdPartyTools();
    }

    // Aktualisiert die Zustimmung für eine bestimmte Kategorie und löst entsprechende Bereinigungsaktionen aus
    public updateConsent(category: ConsentCategory, value: boolean) {
        // ✅ Speichern der aktualisierten Consent-Einstellungen
        this.config.consentConfig[category] = value;
        this.saveConsentConfig();
    
        // ✅ Entferne alles, was nicht erlaubt ist
        this.cleanUpDisallowedEntries();
    
        // ✅ Lade alles, was jetzt erlaubt ist
        this.addThirdPartyTools();
    
        // ✅ Event auslösen, damit andere Komponenten darauf reagieren können
        window.dispatchEvent(new CustomEvent('consentChanged', {
            detail: { category, value, message: 'Consent is changed' }
        }));
    }

    // Gibt die aktuelle Zustimmungskonfiguration zurück
    public getConsent(): ConsentConfig {
        return this.config.consentConfig;
    }
}

export default new ConsentManager();
